import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ISalesStatsByDayResponse,
  IGetWithdrawalStatsResponse,
  IGetTIFStatsResponse,
  IGetWithdrawalRatesResponse,
  IGetTIFRegistrationByLocationResponse,
  IGetSalesResultsInScalesResponse,
  IGetPassersStatsResponse,
  IGetPassedSubscriptionPurchaseDateResponse,
  IGetSpentToPassEvaluationResponse,
} from "../../api/billing/index.types";

export type IFinancialsState = {
  sales_by_days: {
    data: ISalesStatsByDayResponse["data"]["report"];
    loading: boolean;
  };
  withdrawal_stats: {
    data: IGetWithdrawalStatsResponse["data"]["report"];
    loading: boolean;
  };
  tif_stats: {
    data: IGetTIFStatsResponse["data"];
    loading: boolean;
  };
  withdrawal_per_requests: {
    data: IGetWithdrawalRatesResponse["data"];
    loading: boolean;
  };
  withdrawal_per_tif_registration: {
    data: IGetWithdrawalRatesResponse["data"];
    loading: boolean;
  };
  withdrawal_per_sales: {
    data: IGetWithdrawalRatesResponse["data"];
    loading: boolean;
  };
  tif_registration_by_location: {
    data: {
      report: IGetTIFRegistrationByLocationResponse["data"]["report"];
    };
    loading: boolean;
  };
  sales_results_in_scales: {
    data: IGetSalesResultsInScalesResponse["data"]["report"];
    loading: boolean;
  };
  passersStats: {
    data: IGetPassersStatsResponse["data"];
    loading: boolean;
    passedSubscription: {
      data: IGetPassedSubscriptionPurchaseDateResponse["data"];
      loading: boolean;
    };
    spentToPassEvaluation: {
      data: IGetSpentToPassEvaluationResponse["data"]["spentToPassEvaluation"];
      loading: boolean;
    };
  };
};

const initialState: IFinancialsState = {
  sales_by_days: {
    data: [],
    loading: false,
  },
  withdrawal_stats: {
    data: [],
    loading: false,
  },
  tif_stats: {
    data: {
      report: [],
    },
    loading: false,
  },
  withdrawal_per_requests: {
    data: {
      billingHistory: [],
      tifRegistration: [],
      w80: [],
    },
    loading: false,
  },
  withdrawal_per_tif_registration: {
    data: {
      billingHistory: [],
      tifRegistration: [],
      w80: [],
    },
    loading: false,
  },
  withdrawal_per_sales: {
    data: {
      billingHistory: [],
      tifRegistration: [],
      w80: [],
    },
    loading: false,
  },
  tif_registration_by_location: {
    data: {
      report: [],
    },
    loading: false,
  },
  sales_results_in_scales: {
    data: [],
    loading: false,
  },
  passersStats: {
    data: {
      accountsNewPurchases: [],
      billingHistory: [],
      sumBillingHistory: [],
      latestResetAndNewPurchase: [],
    },
    loading: false,
    passedSubscription: {
      data: {
        passedSubscriptionPurchaseDate: [],
      },
      loading: false,
    },
    spentToPassEvaluation: {
      data: [],
      loading: false,
    },
  },
};

const financialsSlice = createSlice({
  name: "financials",
  initialState,
  reducers: {
    // SalesByDays
    fetchSalesByDaysRequest: (state) => {
      state.sales_by_days.loading = true;
    },
    fetchSalesByDaysSuccess: (
      state,
      action: PayloadAction<ISalesStatsByDayResponse["data"]["report"]>
    ) => {
      state.sales_by_days.data = action.payload;
      state.sales_by_days.loading = false;
    },
    fetchSalesByDaysFailed: (state) => {
      state.sales_by_days.loading = false;
    },

    // Withdrawal stats
    fetchWithdrawalStatsRequest: (state) => {
      state.withdrawal_stats.loading = true;
    },
    fetchWithdrawalStatsSuccess: (
      state,
      action: PayloadAction<IGetWithdrawalStatsResponse["data"]["report"]>
    ) => {
      state.withdrawal_stats.data = action.payload;
      state.withdrawal_stats.loading = false;
    },
    fetchWithdrawalStatsFailed: (state) => {
      state.withdrawal_stats.loading = false;
    },

    // TIF stats
    fetchTIFStatsRequest: (state) => {
      state.tif_stats.loading = true;
    },
    fetchTIFStatsSuccess: (state, action: PayloadAction<IGetTIFStatsResponse["data"]>) => {
      state.tif_stats.data = action.payload;
      state.tif_stats.loading = false;
    },
    fetchTIFStatsFailed: (state) => {
      state.tif_stats.loading = false;
    },

    // Withdrawal per requests
    fetchWithdrawalPerRequestsRequest: (state) => {
      state.withdrawal_per_requests.loading = true;
    },
    fetchWithdrawalPerRequestsSuccess: (
      state,
      action: PayloadAction<IGetWithdrawalRatesResponse["data"]>
    ) => {
      state.withdrawal_per_requests.data = action.payload;
      state.withdrawal_per_requests.loading = false;
    },
    fetchWithdrawalPerRequestsFailed: (state) => {
      state.withdrawal_per_requests.loading = false;
    },

    // Withdrawal per TIF registration
    fetchWithdrawalPerTIFRegistrationRequest: (state) => {
      state.withdrawal_per_tif_registration.loading = true;
    },
    fetchWithdrawalPerTIFRegistrationSuccess: (
      state,
      action: PayloadAction<IGetWithdrawalRatesResponse["data"]>
    ) => {
      state.withdrawal_per_tif_registration.data = action.payload;
      state.withdrawal_per_tif_registration.loading = false;
    },
    fetchWithdrawalPerTIFRegistrationFailed: (state) => {
      state.withdrawal_per_tif_registration.loading = false;
    },

    // Withdrawal per Sales
    fetchWithdrawalPerSalesRequest: (state) => {
      state.withdrawal_per_sales.loading = true;
    },
    fetchWithdrawalPerSalesSuccess: (
      state,
      action: PayloadAction<IGetWithdrawalRatesResponse["data"]>
    ) => {
      state.withdrawal_per_sales.data = action.payload;
      state.withdrawal_per_sales.loading = false;
    },
    fetchWithdrawalPerSalesFailed: (state) => {
      state.withdrawal_per_sales.loading = false;
    },

    // TIF registration by location
    fetchTIFRegistrationByLocationRequest: (state) => {
      state.tif_registration_by_location.loading = true;
    },
    fetchTIFRegistrationByLocationSuccess: (
      state,
      action: PayloadAction<IGetTIFRegistrationByLocationResponse["data"]>
    ) => {
      state.tif_registration_by_location.data = action.payload;
      state.tif_registration_by_location.loading = false;
    },
    fetchTIFRegistrationByLocationFailed: (state) => {
      state.tif_registration_by_location.loading = false;
    },

    // Sales results in scales
    fetchSalesResultsInScalesRequest: (state) => {
      state.sales_results_in_scales.loading = true;
    },
    fetchSalesResultsInScalesSuccess: (
      state,
      action: PayloadAction<IGetSalesResultsInScalesResponse["data"]["report"]>
    ) => {
      state.sales_results_in_scales.data = action.payload;
      state.sales_results_in_scales.loading = false;
    },
    fetchSalesResultsInScalesFailed: (state) => {
      state.sales_results_in_scales.loading = false;
    },

    // Passers stats
    fetchPassersStatsRequest: (state) => {
      state.passersStats.loading = true;
    },
    fetchPassersStatsSuccess: (state, action: PayloadAction<IGetPassersStatsResponse["data"]>) => {
      state.passersStats.data = action.payload;
      state.passersStats.loading = false;
    },
    clearPassersStatsState: (state) => {
      state.passersStats.data = {
        accountsNewPurchases: [],
        billingHistory: [],
        sumBillingHistory: [],
        latestResetAndNewPurchase: [],
      };
    },
    fetchPassersStatsFailed: (state) => {
      state.passersStats.loading = false;
    },

    // PassedSubscriptionPurchaseDate
    fetchPassedSubscriptionPurchaseDateRequest: (state) => {
      state.passersStats.passedSubscription.loading = true;
    },
    fetchPassedSubscriptionPurchaseDateSuccess: (
      state,
      action: PayloadAction<IGetPassedSubscriptionPurchaseDateResponse["data"]>
    ) => {
      state.passersStats.passedSubscription.data = action.payload;
      state.passersStats.passedSubscription.loading = false;
    },
    clearPassedSubscriptionPurchaseDateState: (state) => {
      state.passersStats.passedSubscription.data = {
        passedSubscriptionPurchaseDate: [],
      };
    },
    fetchPassedSubscriptionPurchaseDateFailed: (state) => {
      state.passersStats.passedSubscription.loading = false;
    },

    // SpentToPassEvaluation
    fetchSpentToPassEvaluationRequest: (state) => {
      state.passersStats.spentToPassEvaluation.loading = true;
    },
    fetchSpentToPassEvaluationSuccess: (
      state,
      action: PayloadAction<IGetSpentToPassEvaluationResponse["data"]["spentToPassEvaluation"]>
    ) => {
      state.passersStats.spentToPassEvaluation.data = action.payload;
      state.passersStats.spentToPassEvaluation.loading = false;
    },
    clearSpentToPassEvaluationState: (state) => {
      state.passersStats.spentToPassEvaluation.data = [];
    },
    fetchSpentToPassEvaluationFailed: (state) => {
      state.passersStats.spentToPassEvaluation.loading = false;
    },
  },
});

export const financialsActions = financialsSlice.actions;
export default financialsSlice.reducer;
